import { apiRequest } from '@/lib/api-client';
import type { AttachmentHandle, PerSlotPolicy, UploadHandle } from '../types';

interface PresignUploadRequest {
  entityType: string;
  entityId: string;
  category: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  policy: PerSlotPolicy;
}

interface PresignUploadResponse {
  upload_url: string;
  upload_handle: UploadHandle;
  expires_in_seconds: number;
}

export async function presignUpload(
  body: PresignUploadRequest,
  signal?: AbortSignal,
): Promise<PresignUploadResponse> {
  return apiRequest<PresignUploadResponse>('/files/upload-url', {
    method: 'POST',
    body,
    ...(signal ? { signal } : {}),
  });
}

export async function putToStorage(
  uploadUrl: string,
  file: File,
  signal?: AbortSignal,
): Promise<void> {
  const init: RequestInit = {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  };
  if (signal) init.signal = signal;
  const response = await fetch(uploadUrl, init);
  if (!response.ok) {
    throw new Error(`Storage PUT failed: ${String(response.status)}`);
  }
}

export async function registerUpload(
  uploadHandle: UploadHandle,
  signal?: AbortSignal,
): Promise<AttachmentHandle> {
  return apiRequest<AttachmentHandle>('/files', {
    method: 'POST',
    body: { uploadHandle },
    ...(signal ? { signal } : {}),
  });
}

export async function listAttachments(
  query: { entityType: string; entityId: string; category?: string },
  signal?: AbortSignal,
): Promise<AttachmentHandle[]> {
  return apiRequest<AttachmentHandle[]>('/attachments', {
    query,
    ...(signal ? { signal } : {}),
  });
}

export async function getDownloadUrl(
  fileId: string,
  ttlSeconds?: number,
): Promise<{ url: string; expires_in_seconds: number }> {
  return apiRequest('/files/' + fileId + '/download-url', {
    ...(ttlSeconds !== undefined ? { query: { ttl: ttlSeconds } } : {}),
  });
}

export async function softDeleteAttachment(attachmentId: string, reason?: string): Promise<void> {
  await apiRequest('/attachments/' + attachmentId, {
    method: 'DELETE',
    body: reason !== undefined ? { reason } : {},
  });
}
